import classNames from 'classnames'
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { Button } from 'core-components/library/button'
import { Flex } from 'core-components/library/flex'

import { WIDGET_TOUR_TRANSLATION_KEYS as KEYS } from '../../configs/translations'
import { WidgetTourConfig } from '../../models'
import { widgetTourActionRegistry } from '../../shared/widgetTourActionRegistry'
import { WidgetTourText } from '../widgetTourText/WidgetTourText'

import styles from './WidgetTourRenderer.module.scss'

interface IWidgetTourRendererProps {
  config: WidgetTourConfig
  onComplete: () => void
  onDismiss: () => void
}

interface ISize {
  width: number
  height: number
}

const TARGET_PADDING = 8
const TOOLTIP_GAP = 16
const VIEWPORT_PADDING = 16

const requestFrame = (callback: FrameRequestCallback): number => {
  if (typeof window.requestAnimationFrame === 'function') {
    return window.requestAnimationFrame(callback)
  }

  return window.setTimeout(() => callback(performance.now()), 0)
}

const cancelFrame = (frameId: number): void => {
  if (typeof window.cancelAnimationFrame === 'function') {
    window.cancelAnimationFrame(frameId)
    return
  }

  window.clearTimeout(frameId)
}

const getTarget = (targetId: string): HTMLElement | null => {
  return (
    Array.from(document.querySelectorAll<HTMLElement>('[data-tour-id]')).find(
      (element) => element.dataset.tourId === targetId
    ) ?? null
  )
}

/** In translation mode the tooltip holds text fields, and keys typed there are not tour
 *  navigation. */
const isTextField = (target: EventTarget | null): boolean => {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
  )
}

const waitForNextPaint = (): Promise<void> => {
  return new Promise((resolve) => {
    requestFrame(() => {
      requestFrame(() => resolve())
    })
  })
}

const delay = (milliseconds: number): Promise<void> => {
  return new Promise((resolve) => window.setTimeout(resolve, Math.max(milliseconds, 0)))
}

const getTooltipPosition = (targetRect: DOMRect, tooltipSize: ISize) => {
  const maxLeft = Math.max(
    VIEWPORT_PADDING,
    window.innerWidth - tooltipSize.width - VIEWPORT_PADDING
  )
  const maxTop = Math.max(
    VIEWPORT_PADDING,
    window.innerHeight - tooltipSize.height - VIEWPORT_PADDING
  )
  const clampLeft = (left: number) => Math.min(Math.max(left, VIEWPORT_PADDING), maxLeft)
  const clampTop = (top: number) => Math.min(Math.max(top, VIEWPORT_PADDING), maxTop)

  if (window.innerWidth - targetRect.right >= tooltipSize.width + TOOLTIP_GAP) {
    return { left: targetRect.right + TOOLTIP_GAP, top: clampTop(targetRect.top) }
  }

  if (targetRect.left >= tooltipSize.width + TOOLTIP_GAP) {
    return {
      left: targetRect.left - tooltipSize.width - TOOLTIP_GAP,
      top: clampTop(targetRect.top),
    }
  }

  if (window.innerHeight - targetRect.bottom >= tooltipSize.height + TOOLTIP_GAP) {
    return { left: clampLeft(targetRect.left), top: targetRect.bottom + TOOLTIP_GAP }
  }

  return {
    left: clampLeft(targetRect.left),
    top: clampTop(targetRect.top - tooltipSize.height - TOOLTIP_GAP),
  }
}

export const WidgetTourRenderer = ({ config, onComplete, onDismiss }: IWidgetTourRendererProps) => {
  const [stepIndex, setStepIndex] = useState(0)
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)
  const [tooltipSize, setTooltipSize] = useState<ISize>({ width: 320, height: 180 })
  const [targetRevision, setTargetRevision] = useState(0)
  const [isStepPrepared, setIsStepPrepared] = useState(false)

  const directionRef = useRef<'forward' | 'backward'>('forward')
  const tooltipRef = useRef<HTMLDivElement>(null)
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null)
  const maskId = `spotlight-mask-${useId().replace(/:/g, '')}`
  const currentStep = config.steps[stepIndex]
  const isTargetlessStep = Boolean(currentStep && !currentStep.targetId)
  const isTargetVisible = targetRect !== null

  const movePastMissingStep = useCallback(() => {
    if (directionRef.current === 'backward' && stepIndex > 0) {
      setStepIndex((currentIndex) => currentIndex - 1)
      return
    }

    directionRef.current = 'forward'

    if (stepIndex < config.steps.length - 1) {
      setStepIndex((currentIndex) => currentIndex + 1)
      return
    }

    onComplete()
  }, [config.steps.length, onComplete, stepIndex])

  useEffect(() => {
    if (!currentStep) {
      onComplete()
      return
    }

    let isCancelled = false

    const prepareStep = async () => {
      setIsStepPrepared(false)
      setTargetElement(null)
      setTargetRect(null)

      if (currentStep.action) {
        const isActionHandled = await widgetTourActionRegistry.execute(
          config.widgetId,
          currentStep.action
        )

        if (isCancelled) {
          return
        }

        if (!isActionHandled) {
          movePastMissingStep()
          return
        }
      }

      if (currentStep.waitAfterActionMs !== undefined) {
        await delay(currentStep.waitAfterActionMs)
      }

      await waitForNextPaint()

      if (isCancelled) {
        return
      }

      if (!currentStep.targetId) {
        setIsStepPrepared(true)
        return
      }

      const target = getTarget(currentStep.targetId)

      if (isCancelled) {
        return
      }

      if (!target) {
        movePastMissingStep()
        return
      }

      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
      }

      setTargetElement(target)
      setIsStepPrepared(true)
    }

    prepareStep().catch(() => {
      if (!isCancelled) {
        movePastMissingStep()
      }
    })

    return () => {
      isCancelled = true
    }
  }, [config.widgetId, currentStep, movePastMissingStep, onComplete, targetRevision])

  useEffect(() => {
    if (!targetElement) {
      return
    }

    let animationFrameId: number | null = null

    const updateTargetRect = () => {
      if (!document.body.contains(targetElement)) {
        setTargetRevision((revision) => revision + 1)
        return
      }

      setTargetRect(targetElement.getBoundingClientRect())
    }

    const scheduleTargetRectUpdate = () => {
      if (animationFrameId !== null) {
        cancelFrame(animationFrameId)
      }

      animationFrameId = requestFrame(updateTargetRect)
    }

    updateTargetRect()

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleTargetRectUpdate)
    resizeObserver?.observe(targetElement)

    const mutationObserver = new MutationObserver(scheduleTargetRectUpdate)
    mutationObserver.observe(document.body, { childList: true, subtree: true })

    document.addEventListener('scroll', scheduleTargetRectUpdate, true)
    window.addEventListener('resize', scheduleTargetRectUpdate, { passive: true })

    return () => {
      if (animationFrameId !== null) {
        cancelFrame(animationFrameId)
      }
      resizeObserver?.disconnect()
      mutationObserver.disconnect()
      document.removeEventListener('scroll', scheduleTargetRectUpdate, true)
      window.removeEventListener('resize', scheduleTargetRectUpdate)
    }
  }, [targetElement])

  useEffect(() => {
    if (!currentStep?.targetId) {
      return
    }

    const tooltip = tooltipRef.current

    if (!tooltip) {
      return
    }

    const updateTooltipSize = () => {
      const rect = tooltip.getBoundingClientRect()
      setTooltipSize({ width: rect.width, height: rect.height })
    }

    updateTooltipSize()
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateTooltipSize)
    resizeObserver?.observe(tooltip)

    return () => resizeObserver?.disconnect()
  }, [currentStep?.targetId, isTargetVisible, stepIndex])

  const goToPreviousStep = useCallback(() => {
    directionRef.current = 'backward'
    setIsStepPrepared(false)
    setTargetElement(null)
    setTargetRect(null)
    setStepIndex((currentIndex) => Math.max(currentIndex - 1, 0))
  }, [])

  const goToNextStep = useCallback(() => {
    if (stepIndex === config.steps.length - 1) {
      onComplete()
      return
    }

    directionRef.current = 'forward'
    setIsStepPrepared(false)
    setTargetElement(null)
    setTargetRect(null)
    setStepIndex((currentIndex) => currentIndex + 1)
  }, [config.steps.length, onComplete, stepIndex])

  const dismissTour = useCallback(() => {
    onDismiss()
  }, [onDismiss])

  const isKeyboardNavigationAvailable =
    Boolean(currentStep) && isStepPrepared && (isTargetlessStep || targetRect !== null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' && isTextField(event.target)) {
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        dismissTour()
        return
      }

      if (
        isKeyboardNavigationAvailable &&
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight')
      ) {
        event.preventDefault()

        if (event.repeat) {
          return
        }

        if (event.key === 'ArrowLeft') {
          // A step that hides Previous must not be walked back past with the keyboard either.
          if (stepIndex > 0 && !currentStep?.hidePrevious) {
            goToPreviousStep()
          }
          return
        }

        goToNextStep()
        return
      }

      if (event.key !== 'Tab' || !tooltipRef.current) {
        if (event.key === 'Tab') {
          event.preventDefault()
        }
        return
      }

      const focusableElements = Array.from(
        tooltipRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [tabindex="0"]'
        )
      )

      if (!focusableElements.length) {
        event.preventDefault()
        tooltipRef.current.focus()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (
        event.shiftKey &&
        (document.activeElement === firstElement || document.activeElement === tooltipRef.current)
      ) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    currentStep?.hidePrevious,
    dismissTour,
    goToNextStep,
    goToPreviousStep,
    isKeyboardNavigationAvailable,
    stepIndex,
  ])

  useEffect(() => {
    previouslyFocusedElementRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    previouslyFocusedElementRef.current?.blur()

    return () => {
      const previouslyFocusedElement = previouslyFocusedElementRef.current

      if (previouslyFocusedElement && document.body.contains(previouslyFocusedElement)) {
        previouslyFocusedElement.focus()
      }
    }
  }, [])

  // Keyed on the target showing up rather than on its rect: the rect is replaced on every
  // mutation anywhere on the page, and refocusing then would pull focus out of whatever inside
  // the tooltip holds it — a translator's text field included.
  useEffect(() => {
    if (isStepPrepared && (isTargetlessStep || isTargetVisible)) {
      tooltipRef.current?.focus()
    }
  }, [isStepPrepared, isTargetlessStep, stepIndex, isTargetVisible])

  const tooltipPosition = useMemo(() => {
    return targetRect ? getTooltipPosition(targetRect, tooltipSize) : null
  }, [targetRect, tooltipSize])
  const isTooltipVisible =
    Boolean(currentStep) && isStepPrepared && (isTargetlessStep || tooltipPosition !== null)

  const renderStepText = (text: string) =>
    config.translatable ? <WidgetTourText text={text} /> : text

  if (typeof document === 'undefined') {
    return null
  }

  const spotlightRect =
    currentStep?.targetId && targetRect
      ? {
          x: Math.max(targetRect.left - TARGET_PADDING, 0),
          y: Math.max(targetRect.top - TARGET_PADDING, 0),
          width: Math.min(targetRect.width + TARGET_PADDING * 2, window.innerWidth),
          height: Math.min(targetRect.height + TARGET_PADDING * 2, window.innerHeight),
        }
      : null

  return createPortal(
    <div className={styles.root} data-testid="widget-tour">
      {!spotlightRect && <div className={styles.backdrop} />}

      {spotlightRect && (
        <svg className={styles.overlay} aria-hidden="true">
          <defs>
            <mask id={maskId}>
              <rect width="100%" height="100%" fill="white" />
              <rect {...spotlightRect} rx="8" fill="black" />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            className={styles.overlayFill}
            mask={`url(#${maskId})`}
          />
          <rect {...spotlightRect} rx="8" className={styles.outline} />
        </svg>
      )}

      {currentStep && isTooltipVisible && (
        <div
          ref={tooltipRef}
          className={classNames(styles.tooltip, {
            [styles.centeredTooltip]: isTargetlessStep,
          })}
          style={isTargetlessStep ? undefined : (tooltipPosition ?? undefined)}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${maskId}-title`}
          tabIndex={-1}
        >
          <h2 id={`${maskId}-title`} className={styles.title}>
            {renderStepText(currentStep.title)}
          </h2>
          <p className={styles.description}>{renderStepText(currentStep.description)}</p>

          <Flex className={styles.actions} justifyContent="space-between" alignItems="center">
            <Button variant="text" color="grey" onClick={dismissTour}>
              {currentStep.dismissLabel ? (
                renderStepText(currentStep.dismissLabel)
              ) : (
                <WidgetTourText text={KEYS.close} fallback="Close" />
              )}
            </Button>
            <Flex gap="sm">
              {!currentStep.hidePrevious && (
                <Button
                  variant="outlined"
                  color="grey"
                  disabled={stepIndex === 0}
                  onClick={goToPreviousStep}
                >
                  <WidgetTourText text={KEYS.previous} fallback="Previous" />
                </Button>
              )}
              <Button onClick={goToNextStep}>
                {currentStep.nextLabel ? (
                  renderStepText(currentStep.nextLabel)
                ) : stepIndex === config.steps.length - 1 ? (
                  <WidgetTourText text={KEYS.finish} fallback="Finish" />
                ) : (
                  <WidgetTourText text={KEYS.next} fallback="Next" />
                )}
              </Button>
            </Flex>
          </Flex>
        </div>
      )}
    </div>,
    document.body
  )
}
